import { GET, POST, PUT, DELETE, backendURL, assignApiKey } from "./httpAPI";

// Shared SSE reader for live-chat message streams. Parses SSE frames (blocks separated
// by \n\n, lines "event:" and "data:") and calls onEvent(type, data) per frame. When
// `authenticated` is true, attaches the Bearer token (the owner preview endpoints are
// team-scoped, unlike the public widget endpoints). `signal` (AbortSignal) cancels it.
const streamLiveChatSse = async (url, body, authenticated, onEvent, signal) => {
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  };
  if (authenticated) {
    assignApiKey(options);
  }
  const flush = (raw) => {
    let name = "message";
    let hasEvent = false;
    const dataLines = [];
    raw.split("\n").forEach((line) => {
      if (line.startsWith("event:")) {
        name = line.slice(6).trim();
        hasEvent = true;
      } else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    });
    // Deliver a named event even when it carries no data (e.g. "event: done\n\n"); only skip
    // frames that are neither (comments / keepalives). Data-less events get an empty object.
    if (!dataLines.length && !hasEvent) return;
    let payload = dataLines.length ? dataLines.join("\n") : {};
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch (e) {
        /* keep as string */
      }
    }
    onEvent(name, payload);
  };
  try {
    const resp = await fetch(url, options);
    if (!resp.ok || !resp.body) {
      onEvent("error", { message: "Live chat unavailable." });
      return;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        if (frame.trim()) flush(frame);
      }
    }
    if (buffer.trim()) flush(buffer);
  } catch (e) {
    // A deliberate abort (visitor navigated away / switched tabs) is not an error.
    if (signal?.aborted || (e && e.name === "AbortError")) return;
    onEvent("error", { message: "Live chat unavailable." });
  }
};

const backend = {
  register: (request) => {
    return POST("/public/register", request);
  },
  registerAppsumo: (request) => {
    return POST("/public/register/appsumo", request);
  },
  registerPitchGround: (request) => {
    return POST("/public/register/pitchground", request);
  },
  registerDealify: (request) => {
    return POST("/public/register/dealify", request);
  },
  // Attaches an anonymous free-tool run (from /tools on the marketing site) to the signed-in
  // account. Needs the auth token, so it can only run after register/login has stored one.
  claimToolRun: (uid) => {
    return POST("/public/tools/runs/" + uid + "/claim", {});
  },
  // This account's saved tool runs, newest first. Note the path: /tools rather than /public/tools,
  // because it returns one account's history and goes through AuthFilter like any owned resource.
  getMyToolRuns: () => {
    return GET("/tools/runs");
  },
  getInvitation: (uid) => {
    return GET("/public/invitations/" + uid);
  },
  registerByInvitation: (uid, request) => {
    return POST("/public/invitations/" + uid, request);
  },

  loginWithPassword: (username, password) => {
    return POST("/auth/login", { username, password });
  },
  resetPassword: (request) => {
    return POST("/public/forgot-password/reset", request);
  },
  getResetToken: (token) => {
    return GET("/public/forgot-password/reset/" + token);
  },
  finishReset: (token, request) => {
    return POST("/public/forgot-password/reset/" + token, request);
  },
  uploadAsset: (workspaceId, file, filename) => {
    const request = new FormData();
    request.append("file", file);
    request.append("filename", filename);
    return POST("/public/media/" + workspaceId + "/assets", request, true);
  },
  getMediaAsset: (workspaceId, uid) => {
    return GET("/public/media/" + workspaceId + "/assets/" + uid);
  },
  /*
  getMediaAssets: ( workspaceId, page) => {
    return GET("/public/media/" + workspaceId + "/assets?p=" + page);
  },
  getMediaAsset: ( workspaceId, uid) => {
    return GET("/public/media/" + workspaceId + "/assets/" + uid);
  },
  deleteMediaAsset: ( workspaceId, asset) => {
    return POST("/public/media/" + workspaceId + "/assets/" + asset.uid + "/delete");
  },
  updateMediaAssetImage: ( workspaceId, asset, image) => {
    const request = new FormData();
    request.append("file", image);
    return POST("/public/media/" + workspaceId + "/assets/" + asset.uid, request, true);
  },
 
  getUnsplashPhotos: ( workspaceId, page) => {
    return GET("/public/media/" + workspaceId + "/unsplash" + (page ? "?p=" + page : ""));
  },
  searchUnsplashPhotos: ( workspaceId, query, page) => {
    return GET("/public/media/" + workspaceId + "/unsplash/search?q=" + query + "&p=" + page);
  },
  addUnsplashPhoto: ( workspaceId, photo) => {
    return POST("/public/media/" + workspaceId + "/unsplash/add", photo);
  },
   */
  changePassword: (current, next) => {
    return POST("/users/creds", { current, next });
  },
  processOrder: (orderId, orderReference) => {
    return POST("/subscription/order", { orderId, orderReference });
  },
  getOrders: () => {
    return GET("/subscription/orders");
  },
  upgradeSubscription: (plan) => {
    return POST("/subscription/change", { plan });
  },
  changeTrialPlan: (plan) => {
    return POST("/subscription/trial-plan", { plan });
  },
  cancelSubscription: () => {
    return POST("/subscription/cancel", {});
  },

  processCreditsOrder: (orderId, orderReference) => {
    return POST("/credits/order", { orderId, orderReference });
  },
  getAccountCredits: () => {
    return GET("/credits");
  },

  // workspace
  getWorkspaces: () => {
    return GET("/workspaces");
  },
  lookupWorkspace: (templateUid) => {
    return GET("/sdk/workspace-lookup?templateId=" + templateUid);
  },
  createWorkspace: (name) => {
    return POST("/workspaces", { name });
  },
  deleteWorkspace: (uid) => {
    return POST("/workspaces/" + uid + "/delete");
  },
  updateWorkspace: (uid, request) => {
    return POST("/workspaces/" + uid, request);
  },
  // workspace members
  getWorkspaceMembers: (uid) => {
    return GET("/workspaces/" + uid + "/users");
  },
  sendWorkspaceInvite: (workspaceId, { email, isAdmin }) => {
    return POST("/workspaces/" + workspaceId + "/users", {
      email,
      role: isAdmin ? "ADMIN" : "CONTRIBUTOR",
    });
  },
  cancelWorkspaceInvite: (workspaceId, uid) => {
    return POST(
      "/workspaces/" + workspaceId + "/invitations/" + uid + "/delete"
    );
  },
  updateWorkspaceMember: (workspaceId, uid, role) => {
    console.log("updateWorkspaceMember ", uid, role);
    return POST("/workspaces/" + workspaceId + "/users/" + uid, { role });
  },
  deleteWorkspaceMember: (workspaceId, uid) => {
    return POST("/workspaces/" + workspaceId + "/users/" + uid + "/delete");
  },
  // fonts
  getWorkspaceFonts: (uid) => {
    return GET("/workspaces/" + uid + "/fonts");
  },
  createWorkspaceFont: (workspaceId, file) => {
    const request = new FormData();
    request.append("file", file);
    return POST("/workspaces/" + workspaceId + "/fonts", request, true);
  },
  deleteWorkspaceFont: (workspaceId, uid) => {
    return POST("/workspaces/" + workspaceId + "/fonts/" + uid + "/delete");
  },
  // team
  getTeam: () => {
    return GET("/team");
  },
  sendInvite: ({ email, isAdmin }) => {
    return POST("/team", { email, role: isAdmin ? "ADMIN" : "CONTRIBUTOR" });
  },
  cancelInvite: (uid) => {
    return POST("/team/invitations/" + uid + "/delete");
  },
  deleteMember: (uid) => {
    return POST("/team/members/" + uid + "/delete");
  },
  // new
  createCampaign: (name, workspaceId) => {
    return POST("/w/" + workspaceId + "/campaigns", { name });
  },
  getCampaigns: (workspaceId) => {
    return GET("/w/" + workspaceId + "/campaigns");
  },
  getCampaign: (workspaceId, campaignId) => {
    return GET("/w/" + workspaceId + "/campaigns/" + campaignId);
  },
  deleteCampaign: (workspaceId, campaignId) => {
    return DELETE("/w/" + workspaceId + "/campaigns/" + campaignId);
  },
  updateCampaign: (workspaceId, uid, campaign) => {
    return PUT("/w/" + workspaceId + "/campaigns/" + uid, campaign);
  },
  getInterviewCampaign: (campaignId) => {
    return GET("/public/interview-campaigns/" + campaignId);
  },
  createSubmission: (campaignId, name, email) => {
    return POST("/public/interviews", { campaignId, name, email });
  },
  getUserSubmission: (submissionId) => {
    return GET("/public/interviews/" + submissionId);
  },
  updateUserSubmission: (submissionId, submission) => {
    return PUT("/public/interviews/" + submissionId, submission);
  },
  getSubmissions: (workspaceId, campaignId) => {
    return GET(
      "/w/" + workspaceId + "/campaigns/" + campaignId + "/submissions"
    );
  },
  getSubmission: (workspaceId, campaignId, submissionId) => {
    return GET(
      "/w/" +
        workspaceId +
        "/campaigns/" +
        campaignId +
        "/submissions/" +
        submissionId
    );
  },
  deleteSubmission: (workspaceId, campaignId, submissionId) => {
    return DELETE(
      "/w/" +
        workspaceId +
        "/campaigns/" +
        campaignId +
        "/submissions/" +
        submissionId
    );
  },
  saveSubmissionStatus: (workspaceId, campaignId, submissionId, status) => {
    return POST(
      "/w/" +
        workspaceId +
        "/campaigns/" +
        campaignId +
        "/submissions/" +
        submissionId +
        "/status",
      { status }
    );
  },
  /** promptleo new */
  createPrompt: (name, workspaceId) => {
    return POST("/w/" + workspaceId + "/prompts", { name });
  },
  getPrompts: (workspaceId) => {
    return GET("/w/" + workspaceId + "/prompts");
  },
  getPrompt: (workspaceId, id) => {
    return GET("/w/" + workspaceId + "/prompts/" + id);
  },
  deletePrompt: (workspaceId, id) => {
    return DELETE("/w/" + workspaceId + "/prompts/" + id);
  },
  updatePrompt: (workspaceId, uid, prompt) => {
    return PUT("/w/" + workspaceId + "/prompts/" + uid, prompt);
  },
  getPromptVersions: (workspaceId, id) => {
    return GET("/w/" + workspaceId + "/prompts/" + id + "/versions");
  },
  getPromptVersion: (workspaceId, promptId, versionId) => {
    return GET(
      "/w/" + workspaceId + "/prompts/" + promptId + "/versions/" + versionId
    );
  },
  createPromptVersion: (version, workspaceId, promptId) => {
    return POST(
      "/w/" + workspaceId + "/prompts/" + promptId + "/versions",
      version
    );
  },
  updatePromptVersion: (workspaceId, promptUid, version) => {
    return PUT(
      "/w/" +
        workspaceId +
        "/prompts/" +
        promptUid +
        "/versions/" +
        version.uid,
      version
    );
  },
  deletePromptVersion: (workspaceId, promptUid, versionUid) => {
    return DELETE(
      "/w/" + workspaceId + "/prompts/" + promptUid + "/versions/" + versionUid
    );
  },
  getApprovedPrompts: (workspaceId, keyword) => {
    return GET(
      "/w/" +
        workspaceId +
        "/approved-prompts" +
        (keyword ? "?keyword=" + keyword : "")
    );
  },
  /* api keys */
  createApiKey: (apiKey, workspaceId) => {
    return POST("/w/" + workspaceId + "/keys", apiKey);
  },
  getApiKeys: (workspaceId) => {
    return GET("/w/" + workspaceId + "/keys");
  },
  getApiKey: (workspaceId, id) => {
    return GET("/w/" + workspaceId + "/keys/" + id);
  },
  deleteApiKey: (workspaceId, id) => {
    return DELETE("/w/" + workspaceId + "/keys/" + id);
  },
  updateApiKey: (workspaceId, uid, apiKey) => {
    return PUT("/w/" + workspaceId + "/keys/" + uid, apiKey);
  },
  /* forms */
  createForm: (form, workspaceId) => {
    return POST("/w/" + workspaceId + "/forms", form);
  },
  getForms: (workspaceId) => {
    return GET("/w/" + workspaceId + "/forms");
  },
  getForm: (workspaceId, id) => {
    return GET("/w/" + workspaceId + "/forms/" + id);
  },
  deleteForm: (workspaceId, id) => {
    return DELETE("/w/" + workspaceId + "/forms/" + id);
  },
  updateForm: (workspaceId, uid, form) => {
    return PUT("/w/" + workspaceId + "/forms/" + uid, form);
  },
  getFormSubmissions: (workspaceId, id) => {
    return GET("/w/" + workspaceId + "/forms/" + id + "/submissions");
  },
  /** shared forms */
  getSharedForm: (workspaceId, id) => {
    return GET("/public/" + workspaceId + "/shared-forms/" + id);
  },
  getSharedSubmissionForm: (workspaceId, id) => {
    return GET("/public/" + workspaceId + "/shared-form-result/forms/" + id);
  },
  getSharedPromptVersion: (workspaceId, promptId, versionId) => {
    return GET(
      "/public/" +
        workspaceId +
        "/shared-prompts/" +
        promptId +
        "/versions/" +
        versionId
    );
  },
  getSharedFormSubmission: (workspaceId, id, submissionId) => {
    return GET(
      "/public/" +
        workspaceId +
        "/shared-form-result/submissions/" +
        submissionId
    );
  },
  /* endpoints */
  createEndpoint: (endpoint, workspaceId) => {
    return POST("/w/" + workspaceId + "/endpoints", endpoint);
  },
  getEndpoints: (workspaceId) => {
    return GET("/w/" + workspaceId + "/endpoints");
  },
  getEndpoint: (workspaceId, id) => {
    return GET("/w/" + workspaceId + "/endpoints/" + id);
  },
  deleteEndpoint: (workspaceId, id) => {
    return DELETE("/w/" + workspaceId + "/endpoints/" + id);
  },
  updateEndpoint: (workspaceId, uid, endpoint) => {
    return PUT("/w/" + workspaceId + "/endpoints/" + uid, endpoint);
  },
  getEndpointRequests: (workspaceId, id) => {
    return GET("/w/" + workspaceId + "/endpoints/" + id + "/requests");
  },
  execEndpoint: (workspaceId, uid, name, request) => {
    return PUT("/customer/" + workspaceId + "/endpoints/" + name, request);
  },
  /* flows */
  createFlow: (flow, workspaceId) => {
    return POST("/w/" + workspaceId + "/flows", flow);
  },
  getFlows: (workspaceId) => {
    return GET("/w/" + workspaceId + "/flows");
  },
  getFlow: (workspaceId, id) => {
    return GET("/w/" + workspaceId + "/flows/" + id);
  },
  deleteFlow: (workspaceId, id) => {
    return DELETE("/w/" + workspaceId + "/flows/" + id);
  },
  updateFlow: (workspaceId, uid, flow) => {
    return PUT("/w/" + workspaceId + "/flows/" + uid, flow);
  },
  /* models */
  getModel: (workspaceId, modelId) => {
    return GET("/w/" + workspaceId + "/models/" + modelId);
  },
  saveModel: (workspaceId, modelId, request) => {
    return POST("/w/" + workspaceId + "/models/" + modelId, request);
  },
  /* chats */
  getChats: (workspaceId, folderId, keyword) => {
    return GET(
      "/w/" +
        workspaceId +
        "/chats" +
        (folderId ? "?folder_id=" + folderId : "") +
        (keyword ? (folderId ? "&" : "?") + "keyword=" + keyword : "")
    );
  },
  getChat: (workspaceId, chatId) => {
    return GET("/w/" + workspaceId + "/chats/" + chatId);
  },
  createChat: (workspaceId, request) => {
    return POST("/w/" + workspaceId + "/chats", request);
  },
  cloneChat: (chatUid) => {
    return POST("/chats-clone", { chatUid });
  },
  updateChat: (workspaceId, chatId, request) => {
    return PUT("/w/" + workspaceId + "/chats/" + chatId, request);
  },
  postChatMessage: (workspaceId, chatId, message) => {
    return POST("/w/" + workspaceId + "/chats/" + chatId, { content: message });
  },
  saveReply: (workspaceId, chatId, reply) => {
    return POST("/w/" + workspaceId + "/chats/" + chatId + "/reply", reply);
  },
  deleteChat: (workspaceId, id) => {
    return DELETE("/w/" + workspaceId + "/chats/" + id);
  },
  deleteChatMessage: (workspaceId, chatId, messageId) => {
    return DELETE(
      "/w/" + workspaceId + "/chats/" + chatId + "/messages/" + messageId
    );
  },
  updateChatMessage: (workspaceId, chatId, messageId, content) => {
    return PUT(
      "/w/" + workspaceId + "/chats/" + chatId + "/messages/" + messageId,
      { content }
    );
  },
  uploadChatFile: (workspaceId, chatId, file) => {
    const request = new FormData();
    request.append("file", file);
    return POST(
      "/w/" + workspaceId + "/chats/" + chatId + "/upload",
      request,
      true
    );
  },
  lookupWorkspaceBySubdomain: (subdomainOrId) => {
    return GET("/public/workspace-lookup?subdomain=" + subdomainOrId);
  },
  /**** HUB  *****/
  getHubJobs: () => {
    return GET("/jobs");
  },
  getHubJob: (id) => {
    return GET("/jobs/" + id);
  },
  getHubJobsByModel: (model) => {
    return GET("/jobs-by-model?model=" + encodeURIComponent(model));
  },
  /***** CHATBOTS *****/
  getChatbotList: () => {
    return GET("/chatbots");
  },
  getChatbot: (uid) => {
    return GET("/chatbots/" + uid);
  },
  createChatbot: (request) => {
    return POST("/chatbots", request);
  },
  updateChatbot: (data) => {
    return PUT("/chatbots/" + data.uid, data);
  },
  deleteChatbot: (uid) => {
    return DELETE("/chatbots/" + uid);
  },
  indexChatbot: (data) => {
    return POST("/chatbots/" + data.uid + "/index", {});
  },
  getChatMessages: (chatbotUid, chatUid) => {
    return GET("/chatbots/" + chatbotUid + "/chats/" + chatUid + "/messages");
  },
  getKbpages: (chatbotUid) => {
    return GET("/chatbots/" + chatbotUid + "/kbpages");
  },
  /**** Chatbot Widbet *****/
  getWidgetChatbot: (uid, clientId) => {
    return GET("/public/chatbots/" + uid + "?cid=" + clientId);
  },
  createWidgetChat: (chatbotId, request) => {
    return POST("/public/chatbots/" + chatbotId + "/chats", request);
  },
  /***** MCP Playgrounds  ******/
  getPlaygrounds: () => {
    return GET("/playgrounds");
  },
  createPlayground: (request) => {
    return POST("/playgrounds", request);
  },
  savePlayground: (data) => {
    return PUT("/playgrounds/" + data.uid, data);
  },
  getPlayground: (uid) => {
    return GET("/playgrounds/" + uid);
  },
  getPublicPlayground: (uid) => {
    return GET("/public/playgrounds/" + uid);
  },
  regenerateAbout: (uid, payload) => {
    return POST("/playgrounds/" + uid + "/regenerate-about", payload);
  },
  generatePlaygroundExample: (uid, payload) => {
    return POST("/playgrounds/" + uid + "/generate-example", payload);
  },
  deletePlayground: (uid) => {
    return DELETE("/playgrounds/" + uid);
  },
  validateMcpServer: (mcpServerUrl, authType, authToken) => {
    return POST("/playground-validator", { mcpServerUrl, authType, authToken });
  },
  startMcpOAuth: (uid) => {
    return POST("/playgrounds/" + uid + "/oauth/start", {});
  },
  mcpOAuthLogout: (uid) => {
    return POST("/playgrounds/" + uid + "/oauth/logout", {});
  },
  getMcpOAuthStatus: (uid) => {
    return GET("/playgrounds/" + uid + "/oauth/status");
  },

  /***** Live Chat (public widget) ******/
  recordWidgetView: (playgroundUid, clientId) => {
    return POST("/public/live-chat/view", { playgroundUid, clientId }).catch(
      () => {}
    );
  },
  startLiveChatSession: (playgroundUid, clientId, lang, tz, honeypot = "") => {
    return POST("/public/live-chat/start", {
      playgroundUid,
      clientId,
      lang,
      tz,
      honeypot,
    });
  },
  // Streams SSE frames; `onEvent(type, data)` is called per frame. Returns a promise
  // that resolves when the stream ends. `signal` (AbortSignal) cancels it.
  streamLiveChatMessage: (sessionUid, text, clientId, onEvent, signal, honeypot = "") => {
    return streamLiveChatSse(
      backendURL + "/public/live-chat/" + sessionUid + "/message",
      { text, clientId, honeypot },
      false,
      onEvent,
      signal
    );
  },

  // ----- Owner preview (authenticated) -----
  // Starts a preview live-chat session for a playground the caller owns. Preview
  // sessions are flagged server-side and excluded from analytics + monthly quota.
  startLiveChatPreviewSession: (playgroundUid, clientId, lang, tz) => {
    return POST("/playgrounds/" + playgroundUid + "/live-chat/preview/start", {
      clientId,
      lang,
      tz,
    });
  },
  streamLiveChatPreviewMessage: (playgroundUid, sessionUid, text, clientId, onEvent, signal) => {
    return streamLiveChatSse(
      backendURL + "/playgrounds/" + playgroundUid + "/live-chat/preview/" + sessionUid + "/message",
      { text, clientId },
      true,
      onEvent,
      signal
    );
  },

  getLiveChatAnalytics: (uid) => {
    return GET("/playgrounds/" + uid + "/live-chat/analytics");
  },
  getLiveChatSessions: (uid) => {
    return GET("/playgrounds/" + uid + "/live-chat/sessions");
  },
  getLiveChatSession: (uid, sessionUid) => {
    return GET("/playgrounds/" + uid + "/live-chat/sessions/" + sessionUid);
  },
};

export default backend;
